import { createClient } from "@supabase/supabase-js";

// Single shared Supabase client. Credentials come from environment
// variables (see .env.example), never hard-coded. The anon key is safe
// on the client; Row Level Security enforces per-user data isolation.
// Normalize: trim whitespace, strip surrounding quotes, and remove any
// trailing slash. A trailing slash on the URL produces a double slash in the
// REST path (".../rest/v1//table") which PostgREST rejects with PGRST125
// "Invalid path" — even though the auth endpoint tolerates it.
function clean(v: string | undefined): string {
  return (v ?? "")
    .trim()
    .replace(/^["']|["']$/g, "") // surrounding quotes
    .replace(/\/+$/, "") // trailing slash(es)
    .replace(/\/rest\/v1$/i, "") // accidental REST path — supabase-js adds its own
    .replace(/\/+$/, "");
}

const url = clean(import.meta.env.VITE_SUPABASE_URL);
const anonKey = clean(import.meta.env.VITE_SUPABASE_ANON_KEY);

/**
 * The normalized project URL, exported for the one caller that can't go
 * through `supabase.functions.invoke()` — PrintDocumentPage (backlog
 * PRINT-26) has no Supabase session (it's the print-only route Cloudflare's
 * Browser Run service navigates to) and calls print-doc-data via a plain
 * `fetch()` instead, but still needs this same cleaned base URL rather than
 * re-deriving it from the raw env var and risking the two drifting apart.
 */
export const supabaseUrl = url;

if (!url || !anonKey) {
  // Fail loudly in dev if the app is misconfigured, rather than silently
  // making unauthenticated requests.
  console.error(
    "Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Copy .env.example to .env and fill them in.",
  );
}

// Auth session storage: routes between localStorage (persists across browser
// restarts) and sessionStorage (cleared when the tab/browser closes), based
// on the "Keep me signed in" choice made at login. Defaults to persistent to
// match this app's original always-remembered behavior for any sign-in path
// that never calls setRememberMe.
let rememberMe = true;

export function setRememberMe(value: boolean) {
  rememberMe = value;
}

function pickAuthStore(key: string): Storage {
  // A session-only login already active in this tab keeps using sessionStorage
  // even after a reload resets `rememberMe` back to its in-memory default.
  if (sessionStorage.getItem(key) !== null) return sessionStorage;
  return rememberMe ? localStorage : sessionStorage;
}

const authStorage = {
  getItem: (key: string) => localStorage.getItem(key) ?? sessionStorage.getItem(key),
  setItem: (key: string, value: string) => {
    const store = pickAuthStore(key);
    store.setItem(key, value);
    (store === localStorage ? sessionStorage : localStorage).removeItem(key);
  },
  removeItem: (key: string) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  },
};

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storage: authStorage,
  },
});
